import {useMutation} from '@github-ui/react-query'
import {testIdProps} from '@github-ui/test-id-props'
import {BookIcon, LockIcon, PulseIcon, SyncIcon} from '@primer/octicons-react'
import {Button, IconButton, Label, Link, RelativeTime, Spinner} from '@primer/react'
import {Blankslate, SkeletonText} from '@primer/react/experimental'
import {memo, useCallback, useEffect, useMemo, useState} from 'react'

import {apiGenerateRecap} from '../../api/scribe/api-generate-recap'
import {useMarkdownPreview} from '../../hooks/use-markdown-preview'
import {useRecapsQuery} from '../../queries/use-recaps-query'
import {useProjectRouteParams} from '../../router/use-project-route-params'
import {SanitizedMarkdownViewer} from '../common/sanitized-markdown-viewer'
import contentStyles from './content.module.css'
import {SidePanelToolbar} from './header'
import styles from './recap-content.module.css'

export const RecapContent = memo(function RecapContent() {
  const projectRouteParams = useProjectRouteParams()
  const {data, isLoading, error, refetch} = useRecapsQuery({
    variables: projectRouteParams,
    refetchInterval: 10000, // Poll every 10 seconds
  })
  const {html: reportHtml, loadPreview} = useMarkdownPreview()
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(null)

  const hasValidData = data && ((data.insights && data.insights.length > 0) || data.report)

  useEffect(() => {
    if (data?.report?.content) {
      const unescapedContent = data.report.content.replace(/\\n/g, '\n')
      loadPreview(unescapedContent)
    }
  }, [data?.report?.content, loadPreview])

  const {mutate: generateRecap, isPending: isGeneratingRecap} = useMutation({
    mutationFn: apiGenerateRecap,
    onSuccess: () => refetch(),
  })

  const handleGenerateRecap = useCallback(() => {
    setGenerationStartTime(Date.now())
    generateRecap(projectRouteParams)
  }, [generateRecap, projectRouteParams])

  const isRecapRecent = useMemo(() => {
    if (!data?.created_at) return false
    if (!generationStartTime) return false
    const recapTime = new Date(data.created_at).getTime()
    return recapTime > generationStartTime
  }, [data, generationStartTime])

  const showLoadingSpinner = generationStartTime !== null && !isRecapRecent

  const noRecapsAvailable = !isLoading && !error && !hasValidData && !showLoadingSpinner

  return (
    <div className={contentStyles.infoPanelContainer} {...testIdProps('side-panel-recap-content')}>
      <div className={contentStyles.infoPanelContent}>
        <div className={contentStyles.infoPanelHeader}>
          <SidePanelToolbar showCloseButton>
            <div className={styles.infoPanelLinks}>
              <Label variant="accent">Staff</Label>
              <Link
                muted
                href="https://gh.io/copilot-reporting-project-feedback"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Staff feedback"
                className={styles.feedbackLink}
              >
                Feedback
              </Link>
            </div>
          </SidePanelToolbar>
          <div className={contentStyles.headerSpacer} />
          <h2>Recap</h2>
        </div>

        {isLoading && <div>Loading recaps...</div>}

        {error && <div>Failed to load recaps</div>}

        {showLoadingSpinner && (
          <div>
            <Button disabled leadingVisual={<Spinner size="small" />} className={styles.generateRecapLargeButton}>
              Generating recap...
            </Button>

            <div className={styles.insightCard}>
              <div className={styles.skeletonText}>
                <SkeletonText lines={3} aria-label="Generating recap" />
              </div>
              <div className={styles.skeletonText}>
                <SkeletonText lines={3} aria-hidden="true" />
              </div>
              <div className={styles.skeletonText}>
                <SkeletonText lines={3} aria-hidden="true" />
              </div>
            </div>
          </div>
        )}

        {hasValidData && !showLoadingSpinner && (
          <div className={styles.recapsContent}>
            {data.created_at && (
              <div className={styles.recapHeader}>
                <h2 className={styles.insightTitle}>
                  Week of {new Date(data.created_at).toLocaleDateString('en-US', {month: 'short', day: 'numeric'})}
                  <span className={styles.recapDate}>
                    Generated <RelativeTime datetime={data.created_at} /> · <LockIcon size={12} /> Only visible to you
                  </span>
                </h2>
                <div className={styles.recapActions}>
                  <IconButton
                    aria-label="Regenerate recap"
                    icon={SyncIcon}
                    size="small"
                    onClick={handleGenerateRecap}
                    disabled={isGeneratingRecap}
                  />
                </div>
              </div>
            )}

            {data.insights && data.insights.length > 0 && (
              <div className={styles.insightCard}>
                <div className={styles.insightHeader}>
                  <h3 className={styles.widgetTitle}>Insights</h3>
                </div>
                <ul className={styles.insightList}>
                  {data.insights.map(insight => (
                    <li key={insight.id} className={styles.insightItem}>
                      <PulseIcon size={16} aria-hidden="true" className={styles.insightIcon} />
                      <span className={styles.insightText}>{insight.content}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data.report && reportHtml && (
              <div className={styles.insightCard}>
                <div className={styles.reportContent}>
                  <SanitizedMarkdownViewer unverifiedHTML={reportHtml} openLinksInNewTab />
                </div>
              </div>
            )}
          </div>
        )}

        {noRecapsAvailable && (
          <div className={styles.insightCard}>
            <Blankslate>
              <Blankslate.Visual>
                <BookIcon size="medium" />
              </Blankslate.Visual>
              <Blankslate.Heading>Turn recent activity into insights</Blankslate.Heading>
              <Blankslate.Description>
                Get a quick summary and actionable insights from recent work in this project.
              </Blankslate.Description>
              <Blankslate.PrimaryAction
                onClick={handleGenerateRecap}
                disabled={isGeneratingRecap}
                className={styles.generateRecapLargeButton}
              >
                {isGeneratingRecap ? 'Generating recap...' : 'Generate recap'}
              </Blankslate.PrimaryAction>
            </Blankslate>
          </div>
        )}
      </div>
    </div>
  )
})

try{ RecapContent.displayName ||= 'RecapContent' } catch {}